import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export const commaSeperateTrackingNumber = (
  control: AbstractControl,
): { [key: string]: unknown } | null => {
  if (!control?.value) {
    return null;
  }

  return validatingTrackingNumber(control.value)
    ? { invalidTrackingNumber: { value: control.value } }
    : null;
};

export const validatingTrackingNumber = (value: string) => {
  const trackingNumbers = value.split(',');
  const forbidden = trackingNumbers.some(
    (trackingNumber: string) => trackingNumber.length > 18,
  );
  return forbidden;
};

export const isAnyInvalidTrackingNumber = (value: string) => {
  const trackingNumbers = value?.split(',');
  const forbidden = trackingNumbers.some(
    (trackingNumber: string) => trackingNumber.length < 18,
  );
  return forbidden;
};

export const locationSecondCharacterValidation = (
  control: AbstractControl,
): { [key: string]: unknown } | null => {
  if (!control?.value) {
    return null;
  }
  const hasValidCharacter = (control.value as string).charAt(1).includes('W');
  return hasValidCharacter
    ? null
    : {
        invalidLocation: true,
      };
};

export const nonZeroValidator = () => {
  return (control: AbstractControl): ValidationErrors | null => {
    return control.value && parseFloat(control.value) < 1
      ? { nonZero: true }
      : null;
  };
};

export const rangeValidator = (min: number, max: number) => {
  return (control: AbstractControl): ValidationErrors | null => {
    if (
      control.value !== null &&
      (isNaN(control.value) || control.value < min || control.value > max)
    ) {
      return { range: { min, max, actual: control.value } };
    }
    return null;
  };
};

export const maxValidator = (max: number) => {
  return (control: AbstractControl): ValidationErrors | null => {
    if (
      control.value !== null &&
      !isNaN(control.value) &&
      control.value > max
    ) {
      return { maxError: { max, actual: control.value } };
    }
    return null;
  };
};

export function dimensionValidator(min: number, max: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const minRange = Number.isInteger(min) ? `${min}.0` : min;
    const maxRange = Number.isInteger(max) ? `${max}.0` : max;

    const length = parseFloat(control.get('lengthInput')?.value) || 0;
    const width = parseFloat(control.get('widthInput')?.value) || 0;
    const height = parseFloat(control.get('heightInput')?.value) || 0;

    const pallet =
      control.get('palletCheckBox')?.value ||
      control.get('pallet')?.value ||
      false;
    const minValueCheck = length < min || width < min || height < min;
    const maxValueCheck = length > max || width > max || height > max;
    const validationCheck = pallet
      ? minValueCheck || maxValueCheck
      : minValueCheck;

    if (validationCheck) {
      return {
        dimensionError: { min: minRange, max: maxRange, actual: control.value },
      };
    }
    return null;
  };
}

export function weightValidator(min: number, max: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const weight = parseFloat(control.get('weight')?.value) || 0;
    const lb = parseFloat(control.get('lb')?.value) || 0;
    const totalWeight = weight + lb / 10;
    const minRange = Number.isInteger(min) ? `${min}.0` : min;
    const maxRange = Number.isInteger(max) ? `${max}.0` : max;
    if (totalWeight < min) {
      return {
        weightError: { min: minRange, max: maxRange, actual: control.value },
      };
    }
    return null;
  };
}
export class PtnRangePackageValidator {
  static readonly oneZedPattern = /^1Z[0-9A-Z]{8}[0-9]{8}$/;
  static readonly threeZedPattern = /^3Z[0-9A-Z]{8}[0-9]{8}$/;
  static readonly elevenDigitPattern = /^[0-9AC-Z][0-9]{10}$/;
  static readonly elevenZDigitPattern = /^Z[0-9AC-Z][0-9]{9}$/;

  static convertCharacterToDigit(c: string) {
    const lookup = {
      '0': 0,
      '1': 1,
      '2': 2,
      '3': 3,
      '4': 4,
      '5': 5,
      '6': 6,
      '7': 7,
      '8': 8,
      '9': 9,
      A: 2,
      B: 3,
      C: 4,
      D: 5,
      E: 6,
      F: 7,
      G: 8,
      H: 9,
      I: 0,
      J: 1,
      K: 2,
      L: 3,
      M: 4,
      N: 5,
      O: 6,
      P: 7,
      Q: 8,
      R: 9,
      S: 0,
      T: 1,
      U: 2,
      V: 3,
      W: 4,
      X: 5,
      Y: 6,
      Z: 7,
    } as Record<string, number>;

    if (!(c in lookup)) {
      throw new Error('Illegal character: ' + c);
    }

    return lookup[c];
  }

  static calculate(s: string): number {
    s = s.toUpperCase();
    const chars = s.split('');
    let total = 0;
    for (let i = 0; i < chars.length; i++) {
      const value = PtnRangePackageValidator.convertCharacterToDigit(chars[i]);
      if (i % 2 === 0) {
        total += value;
      } else {
        total += 2 * value;
      }
    }
    let checkDigit = 10 - (total % 10);
    if (checkDigit === 10) {
      checkDigit = 0;
    }
    return checkDigit;
  }

  static trackingNumberValidator() {
    return (control: AbstractControl) => {
      const value = control?.value;
      if (!value) {
        return null;
      }
      if (!PtnRangePackageValidator.isValidLengthAndPattern(value)) {
        return { invalid: true };
      }

      const mid =
        value.length === 18 ? value.substring(2, 17) : value.substring(1, 10);
      const checkDigit = PtnRangePackageValidator.calculate(mid);

      return value.endsWith(checkDigit.toString()) ? null : { invalid: true };
    };
  }

  static isValidLengthAndPattern(value: string) {
    if (value.length === 18) {
      return (
        PtnRangePackageValidator.oneZedPattern.test(value) ||
        PtnRangePackageValidator.threeZedPattern.test(value)
      );
    }
    if (value.length === 11) {
      return (
        PtnRangePackageValidator.elevenDigitPattern.test(value) &&
        !PtnRangePackageValidator.elevenZDigitPattern.test(value)
      );
    }
    return false;
  }
}

export interface TrackingFormatRule {
  prefix: string;
  suffixLength: number;
  label?: string; // optional, for better error messages
}

export function validateMultipleTrackingFormats(
  formats: TrackingFormatRule[],
  errorKey: string = 'invalidTrackingNumberFormat',
): ValidatorFn {
  const patterns = formats.map((format) => ({
    ...format,
    regex: new RegExp(`^${format.prefix}[A-Z0-9]{${format.suffixLength}}$`),
  }));

  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;

    const match = patterns.find((p) => p.regex.test(value));
    if (match) return null;
    if (value.length == 11) {
      return PtnRangePackageValidator.trackingNumberValidator();
    }
    return {
      [errorKey]: {
        allowedFormats: patterns.map((p) => ({
          prefix: p.prefix,
          suffixLength: p.suffixLength,
          example: `${p.prefix}${'X'.repeat(p.suffixLength)}`,
          label: p.label || `${p.prefix}[A-Z0-9]{${p.suffixLength}}`,
        })),
        actualValue: value,
        message: `Value must match one of the allowed tracking formats.`,
      },
    };
  };
}
